/**
 * 一番賞開獎揭曉的純決策函式。DOM、jQuery、Peel 實例的生命週期全留給呼叫端，
 * 這裡只回答「走哪條路、每張籤紙顯示什麼、總覽列什麼」。
 */

const GOLD_LEVELS = ['A', 'B', 'C', 'D'];
const SILVER_LEVELS = ['E', 'F'];

/**
 * box_level 對到卡面等級。集單模式的 payload 根本沒有這個鍵，缺漏時回銅卡不丟錯。
 * @param {?string} boxLevel
 * @returns {'gold'|'silver'|'bronze'}
 */
export function cardTier(boxLevel) {
    if (GOLD_LEVELS.includes(boxLevel)) {
        return 'gold';
    }

    if (SILVER_LEVELS.includes(boxLevel)) {
        return 'silver';
    }

    return 'bronze';
}

/**
 * 決定這次開獎要走哪條路。集單模式優先於籤紙：獎項在集單結束前不揭曉。判斷吃 payload
 * 而非頁面載入時的商品 meta——遮蔽與走法必須同一個真相源，否則商品設定中途被改就分岔。
 *
 * 旗標與遮蔽由同一處產出，所以沒有旗標的 payload 一律是未遮蔽的，走一般揭曉。
 * @param {?{is_group_buying?: boolean}} result 開獎結果 payload
 * @param {boolean} hasPeel 頁面上有沒有載到 Peel 函式庫
 * @returns {'group-buy'|'overview'|'peel'}
 */
export function revealMode(result, hasPeel) {
    if (result && result.is_group_buying) {
        return 'group-buy';
    }

    return hasPeel ? 'peel' : 'overview';
}

/**
 * 籤紙彈窗逐張要顯示的資料。
 * @param {{boxes?: Array<object>}} result 開獎結果 payload
 * @returns {Array<{boxNumber: number, levelLabel: (number|string), prizeName: string, tier: string}>}
 */
export function revealSteps(result) {
    return (result && result.boxes || []).map(function(box) {
        return {
            boxNumber: box.box_number,
            levelLabel: box.box_level_name,
            prizeName: box.box_name,
            tier: cardTier(box.box_level),
        };
    });
}

/**
 * 總覽彈窗的檢視模型。沒有最後賞／免單時對應文字為空字串，呼叫端照樣塞得進去。
 * @param {{boxes?: Array<object>, final_reward?: ?string, treasure?: ?{drawn_order?: ?number, return_points?: number,
 *            stop?: ?{unopened_draws: number, refund_points: number, return_points: number}}}} result
 * @returns {{items: Array<{boxNumber: number, levelLabel: (number|string), prizeName: string, coverPhoto: ?string}>,
 *            finalRewardText: string, treasureText: string}}
 */
export function overviewModel(result) {
    const items = (result && result.boxes || []).map(function(box) {
        return {
            boxNumber: box.box_number,
            levelLabel: box.box_level_name,
            prizeName: box.box_name,
            coverPhoto: box.cover_photo === undefined ? null : box.cover_photo,
        };
    });

    const finalReward = result && result.final_reward;

    return {
        items: items,
        finalRewardText: finalReward ? '獲得最後賞：' + finalReward : '',
        treasureText: treasureText(result),
    };
}

/**
 * 免單訊息。沒中免單時為空字串。停止時只開了 k 抽、命中的是最後一筆，所以 k = boxes.length；
 * 玩瘋券取 stop 內「本單退回」，頂層 return_points 含同日前幾單的連帶退款，不顯示。
 * @param {?{boxes?: Array<object>, treasure?: ?{drawn_order?: ?number, return_points?: number,
 *            stop?: ?{unopened_draws: number, refund_points: number, return_points: number}}}} result
 * @returns {string}
 */
function treasureText(result) {
    const treasure = result && result.treasure;

    if (treasure && treasure.stop) {
        const stop = treasure.stop;

        return '第 ' + (result.boxes || []).length + ' 抽中免單，退回 ' + stop.return_points
            + ' 玩瘋券；剩餘 ' + stop.unopened_draws + ' 抽未開，退回 ' + stop.refund_points + ' 點數';
    }

    return treasure && treasure.drawn_order
        ? '恭喜免單！免單金額：' + treasure.return_points
        : '';
}

/**
 * 集單模式的檢視模型。boxes 每筆只讀 box_number；免單只帶抽出順序與金額、不揭露獎項，
 * 集單模式照樣顯示。
 * @param {{boxes?: Array<{box_number: number}>, treasure?: ?{drawn_order?: ?number, return_points?: number,
 *            stop?: ?{unopened_draws: number, refund_points: number, return_points: number}}}} result
 * @returns {{boxNumbers: Array<number>, message: string, treasureText: string}}
 */
export function groupBuyModel(result) {
    return {
        boxNumbers: (result && result.boxes || []).map(function(box) {
            return box.box_number;
        }),
        message: '集單完成，開獎結果將於集單結束後公佈',
        treasureText: treasureText(result),
    };
}
